import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { FaUsers, FaCalendarAlt, FaUser, FaFileInvoiceDollar } from 'react-icons/fa';
import axios from '../api/axios';

const StatCard = ({ title, value, icon, color }) => (
  <div
    className={`bg-gradient-to-r ${color} text-white p-6 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105`}
  >
    <div className="flex justify-between items-center">
      <div>
        <h3 className="text-lg font-medium mb-2">{title}</h3>
        <p className="text-3xl font-bold">{value}</p>
      </div>
      <div className="text-5xl opacity-80">{icon}</div>
    </div>
  </div>
);

const Card = ({ children, title, description }) => (
  <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300">
    <h3 className="text-xl font-semibold mb-2">{title}</h3>
    <p className="text-sm text-gray-600 mb-4">{description}</p>
    {children}
  </div>
);

const statusLabels = {
  OPEN: 'Abierto',
  CLOSED: 'Cerrado',
  ABSENCE_VACATION: 'Vacaciones',
  ABSENCE_SICK: 'Enfermedad',
  ABSENCE_UNPAID: 'Sin sueldo',
  ABSENCE_PERSONAL: 'Personal',
  ABSENCE_OTHER: 'Otra',
  NO_RECORD: 'Sin registro',
};

export default function Dashboard() {
  const today = new Date().toISOString().slice(0, 10);
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [pendingAbsencesCount, setPendingAbsencesCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [employeesResponse, recordsResponse, absencesResponse] = await Promise.all([
          axios.get('/employees'),
          axios.get('/attendance', { params: { from: today, to: today } }),
          axios.get('/absences'),
        ]);
        setEmployees(employeesResponse.data);
        setRecords(recordsResponse.data);
        setPendingAbsencesCount(
          absencesResponse.data.filter((absence) => absence.status === 'REQUESTED').length,
        );
      } catch (err) {
        const message =
          err.response?.data?.message || 'No se pudieron cargar los datos del dashboard.';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [today]);

  const stats = useMemo(() => {
    const recordByEmployee = new Map(
      records.filter((record) => record.date === today).map((record) => [
        record.employeeId,
        record,
      ]),
    );

    let presentCount = 0;
    let absentCount = 0;
    let lateCount = 0;
    let openCount = 0;

    employees.forEach((employee) => {
      const record = recordByEmployee.get(employee.id);
      if (!record) {
        absentCount += 1;
        return;
      }
      if (record.status === 'OPEN') {
        presentCount += 1;
        openCount += 1;
      } else if (record.status === 'CLOSED') {
        presentCount += 1;
      } else if (record.status && record.status.startsWith('ABSENCE')) {
        absentCount += 1;
      }
      if (record.lateMinutes > 0) {
        lateCount += 1;
      }
    });

    return {
      totalEmployees: employees.length,
      presentCount,
      absentCount,
      lateCount,
      openCount,
    };
  }, [employees, records, today]);

  const chartData = [
    { name: 'Presentes', value: stats.presentCount, color: '#2563eb' },
    { name: 'Ausentes', value: stats.absentCount, color: '#f59e0b' },
    { name: 'Abiertos', value: stats.openCount, color: '#16a34a' },
    { name: 'Retrasos', value: stats.lateCount, color: '#dc2626' },
  ];

  const departmentStats = useMemo(() => {
    const recordByEmployee = new Map(
      records.filter((record) => record.date === today).map((record) => [
        record.employeeId,
        record,
      ]),
    );
    const statsByDepartment = new Map();

    employees.forEach((employee) => {
      const departmentLabel =
        employee.departmentName ||
        (employee.departmentId ? `Departamento ${employee.departmentId}` : 'Sin departamento');
      if (!statsByDepartment.has(departmentLabel)) {
        statsByDepartment.set(departmentLabel, {
          department: departmentLabel,
          total: 0,
          present: 0,
          absent: 0,
          late: 0,
        });
      }

      const summary = statsByDepartment.get(departmentLabel);
      summary.total += 1;

      const record = recordByEmployee.get(employee.id);
      if (!record) {
        summary.absent += 1;
        return;
      }

      if (record.status === 'OPEN' || record.status === 'CLOSED') {
        summary.present += 1;
      } else if (record.status && record.status.startsWith('ABSENCE')) {
        summary.absent += 1;
      }

      if (record.lateMinutes > 0) {
        summary.late += 1;
      }
    });

    return Array.from(statsByDepartment.values()).sort((a, b) =>
      a.department.localeCompare(b.department),
    );
  }, [employees, records, today]);

  const lateList = useMemo(() => {
    return records
      .filter((record) => record.date === today && record.lateMinutes > 0)
      .map((record) => {
        const employee = employees.find((item) => item.id === record.employeeId);
        return {
          id: record.id,
          employee: employee ? `${employee.firstName} ${employee.lastName}` : 'Sin nombre',
          lateMinutes: record.lateMinutes,
          status: record.status,
        };
      });
  }, [employees, records, today]);

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-4xl font-bold mb-8 text-center text-gray-800 font-serif">
        Control presencial
      </h1>

      {error && <div className="mb-6 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">{error}</div>}

      {isLoading ? (
        <div className="rounded-lg bg-white p-6 text-sm text-gray-500 shadow-sm">
          Cargando indicadores...
        </div>
      ) : (
        <>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-5 mb-8">
            <StatCard
              title="Total empleados"
              value={stats.totalEmployees}
              icon={<FaUsers />}
              color="from-blue-600 to-blue-800"
            />
            <StatCard
              title="Presentes hoy"
              value={stats.presentCount}
              icon={<FaUser />}
              color="from-green-600 to-green-800"
            />
            <StatCard
              title="Ausentes hoy"
              value={stats.absentCount}
              icon={<FaCalendarAlt />}
              color="from-yellow-500 to-orange-600"
            />
            <StatCard
              title="Retrasos"
              value={stats.lateCount}
              icon={<FaFileInvoiceDollar />}
              color="from-red-500 to-rose-600"
            />
            <StatCard
              title="Ausencias pendientes"
              value={pendingAbsencesCount}
              icon={<FaCalendarAlt />}
              color="from-purple-500 to-indigo-600"
            />
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <Card title="Estado del dia" description={`Resumen del ${today}`}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value">
                    {chartData.map((entry) => (
                      <Cell key={`cell-${entry.name}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Resumen por departamento
                </h4>
                {departmentStats.length === 0 ? (
                  <p className="text-sm text-gray-500">No hay departamentos con datos.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm text-gray-700">
                      <thead className="text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold">Departamento</th>
                          <th className="px-3 py-2 text-center font-semibold">Empleados</th>
                          <th className="px-3 py-2 text-center font-semibold">Presentes</th>
                          <th className="px-3 py-2 text-center font-semibold">Ausentes</th>
                          <th className="px-3 py-2 text-center font-semibold">Retrasos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {departmentStats.map((row) => (
                          <tr key={row.department} className="border-t border-gray-100">
                            <td className="px-3 py-2 text-left">{row.department}</td>
                            <td className="px-3 py-2 text-center font-medium">{row.total}</td>
                            <td className="px-3 py-2 text-center">{row.present}</td>
                            <td className="px-3 py-2 text-center">{row.absent}</td>
                            <td className="px-3 py-2 text-center text-red-600">{row.late}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Card>

            <Card title="Retrasos destacados" description="Colaboradores con minutos de retraso">
              {lateList.length === 0 ? (
                <p className="text-sm text-gray-500">No hay retrasos registrados.</p>
              ) : (
                <div className="space-y-3">
                  {lateList.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between rounded-lg border border-gray-100 p-3"
                    >
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{item.employee}</p>
                        <p className="text-xs text-gray-500">{statusLabels[item.status] || item.status}</p>
                      </div>
                      <span className="text-sm font-semibold text-red-600">
                        {item.lateMinutes} min
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
